// src/pages/Product/EditProduct.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import categoryService from '../../services/category.service';
import productService from '../../services/product.service';
import '../../styles/AddPages.css';

const EditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const [formData, setFormData] = useState({
    companyId: '', companyName: '', categoryId: '',
    productName: '', productAndPacking: '',
    ratePerUnit: '', itemPerBox: '', ratePerBox: '',
    status: 'Available', briefDescription: '', photo: null,
  });
  const [categories, setCategories] = useState([]);
  const [errors, setErrors]         = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading]       = useState(true);

  // Load existing product
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await productService.getProductById(id);
        if (res.success) {
          const p = res.data;
          const companyId   = p.company?.id   || p.companyId   || '';
          const companyName = p.company?.name || p.companyName || '';
          setFormData({
            companyId, companyName,
            categoryId:       String(p.category?.id || p.categoryId || ''),
            productName:      p.productName      || '',
            productAndPacking:p.productAndPacking|| '',
            ratePerUnit:      String(p.ratePerUnit  || ''),
            itemPerBox:       String(p.itemPerBox   || ''),
            ratePerBox:       String(p.ratePerBox   || ''),
            status:           p.status             || 'Available',
            briefDescription: p.briefDescription   || '',
            photo: null,
          });
          if (p.photo) setPhotoPreview(p.photo);
          if (companyId) {
            const catRes = await categoryService.getAllCategories(companyId);
            if (catRes.success) {
              const cats = catRes.data?.categories || catRes.data || [];
              setCategories(Array.isArray(cats) ? cats : []);
            }
          }
        }
      } catch {
        alert('Failed to load product');
        navigate('/product/view');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      const file = files[0];
      setFormData(prev => ({ ...prev, [name]: file }));
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => setPhotoPreview(reader.result);
        reader.readAsDataURL(file);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!formData.categoryId)              e.categoryId       = 'Category is required';
    if (!formData.productName.trim())      e.productName      = 'Product name is required';
    if (!formData.productAndPacking.trim())e.productAndPacking= 'Product & packing is required';
    if (!formData.ratePerUnit || parseFloat(formData.ratePerUnit) <= 0) e.ratePerUnit = 'Valid rate per unit is required';
    if (!formData.itemPerBox  || parseInt(formData.itemPerBox)   <= 0)  e.itemPerBox  = 'Valid items per box is required';
    if (!formData.ratePerBox  || parseFloat(formData.ratePerBox) <= 0)  e.ratePerBox  = 'Valid rate per box is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const payload = {
        productName:       formData.productName.trim(),
        productAndPacking: formData.productAndPacking.trim(),
        ratePerUnit:       parseFloat(formData.ratePerUnit),
        itemPerBox:        parseInt(formData.itemPerBox),
        ratePerBox:        parseFloat(formData.ratePerBox),
        status:            formData.status,
        briefDescription:  formData.briefDescription?.trim() || '',
        categoryId:        parseInt(formData.categoryId),
        companyId:         parseInt(formData.companyId),
      };
      // Only send photo if user picked a new one
      if (formData.photo) payload.photo = photoPreview;

      const res = await productService.updateProduct(id, payload);
      if (res.success) {
        alert('Product updated successfully!');
        navigate(`/product/view/${id}`);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update product');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <div style={{ padding: 60, textAlign: 'center', color: '#6c757d' }}>Loading product...</div>;

  return (
    <div className="add-product-page">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/dashboard/overview">Dashboard</a></li>
          <li className="breadcrumb-item"><a href="/product/view">Products</a></li>
          <li className="breadcrumb-item"><a href={`/product/view/${id}`}>{formData.productName}</a></li>
          <li className="breadcrumb-item active">Edit</li>
        </ol>
      </nav>

      <div className="page-header">
        <h4 className="page-title">Edit Product</h4>
      </div>

      <div className="form-card">
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <div className="form-row">
              {/* Company — read only */}
              <div className="form-group">
                <label className="form-label">Company</label>
                <input type="text" className="form-input" value={formData.companyName} readOnly disabled
                  style={{ backgroundColor: '#e6fffa', color: '#234e52', fontWeight: 500 }} />
              </div>

              {/* Category */}
              <div className="form-group">
                <label className="form-label">Category <span className="required">*</span></label>
                <select name="categoryId" className={`form-input ${errors.categoryId ? 'error' : ''}`}
                  value={formData.categoryId} onChange={handleChange}>
                  <option value="">Select Category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {errors.categoryId && <span className="error-message">{errors.categoryId}</span>}
              </div>

              {/* Product Name */}
              <div className="form-group">
                <label className="form-label">Product Name <span className="required">*</span></label>
                <input type="text" name="productName"
                  className={`form-input ${errors.productName ? 'error' : ''}`}
                  value={formData.productName} onChange={handleChange} placeholder="Enter product name" />
                {errors.productName && <span className="error-message">{errors.productName}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Product & Packing <span className="required">*</span></label>
                <input type="text" name="productAndPacking"
                  className={`form-input ${errors.productAndPacking ? 'error' : ''}`}
                  value={formData.productAndPacking} onChange={handleChange} placeholder="e.g., 500ml bottle" />
                {errors.productAndPacking && <span className="error-message">{errors.productAndPacking}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Rate per Unit <span className="required">*</span></label>
                <input type="number" name="ratePerUnit"
                  className={`form-input ${errors.ratePerUnit ? 'error' : ''}`}
                  value={formData.ratePerUnit} onChange={handleChange} step="0.01" min="0" />
                {errors.ratePerUnit && <span className="error-message">{errors.ratePerUnit}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Item per Box <span className="required">*</span></label>
                <input type="number" name="itemPerBox"
                  className={`form-input ${errors.itemPerBox ? 'error' : ''}`}
                  value={formData.itemPerBox} onChange={handleChange} min="1" />
                {errors.itemPerBox && <span className="error-message">{errors.itemPerBox}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Rate per Box <span className="required">*</span></label>
                <input type="number" name="ratePerBox"
                  className={`form-input ${errors.ratePerBox ? 'error' : ''}`}
                  value={formData.ratePerBox} onChange={handleChange} step="0.01" min="0" />
                {errors.ratePerBox && <span className="error-message">{errors.ratePerBox}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select name="status" className="form-input" value={formData.status} onChange={handleChange}>
                  <option value="Available">Available</option>
                  <option value="Out of Stock">Out of Stock</option>
                  <option value="Discontinued">Discontinued</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group form-group-full">
                <label className="form-label">Brief Description</label>
                <textarea name="briefDescription" className="form-textarea" rows="3"
                  value={formData.briefDescription} onChange={handleChange} placeholder="Enter product description" />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Product Photo</label>
                <input type="file" name="photo" className="form-input" onChange={handleChange} accept="image/*" />
                {photoPreview && (
                  <div className="photo-preview-large">
                    <img src={photoPreview} alt="Preview" />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary"
              onClick={() => navigate(`/product/view/${id}`)} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : '✓ Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProduct;